import { useState, type FC } from 'react'

import { F0Dialog } from '@factorialco/f0-react'
import { Textarea } from '@factorialco/f0-react/dist/experimental'
import {
  BulkRejectReviewRequestDocument,
  PurchaseRequestDetailDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface RejectionReasonModalProps {
  isOpen: boolean
  onClose: () => void
  reviewRequestIds: string[]
  onSuccess?: () => void
}

const RejectionReasonModal: FC<RejectionReasonModalProps> = ({
  isOpen,
  onClose,
  reviewRequestIds,
  onSuccess,
}) => {
  const { company, employee } = useCurrentSession()

  const [bulkRejectReviewRequest, { loading }] = useMutation(BulkRejectReviewRequestDocument, {
    extractorFn: (data) => data.approvalsV2Mutations.bulkRejectApprovalsv2ReviewRequest,
    refetchQueries: [PurchaseRequestDetailDocument],
  })

  const [reason, setReason] = useState('')

  const handleClose = () => {
    setReason('')
    onClose()
  }

  const handleSubmit = async () => {
    const result = await bulkRejectReviewRequest({
      variables: {
        companyId: company.id,
        reviewRequestIds: reviewRequestIds,
        reviewerId: employee.id,
        reason: reason || undefined,
      },
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('procurement.purchase_requests.detail.responses.reject.error'),
      })
    })

    if (handled.success) {
      handleClose()
      onSuccess?.()
    }
  }

  return (
    <F0Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title={i18n.t('procurement.purchase_requests.detail.rejection_reason_modal.title')}
      primaryAction={{
        label: i18n.t('procurement.purchase_requests.detail.rejection_reason_modal.submit'),
        onClick: handleSubmit,
        loading: loading,
        disabled: loading,
      }}
    >
      <Textarea
        label={i18n.t('procurement.purchase_requests.detail.rejection_reason_modal.title')}
        hideLabel
        placeholder={i18n.t(
          'procurement.purchase_requests.detail.rejection_reason_modal.placeholder'
        )}
        value={reason}
        onChange={setReason}
      />
    </F0Dialog>
  )
}

export default RejectionReasonModal
